import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Droplets, 
  FileText, 
  Lightbulb, 
  Shield, 
  Calendar,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const services = [
  {
    icon: Droplets,
    title: 'Professional Irrigation Installation',
    description: 'Complete sprinkler system installation tailored to your landscape. We use only premium quality parts from trusted manufacturers to ensure your system performs flawlessly for years to come.',
    image: 'https://images.unsplash.com/photo-1768796373541-fabc238c6eec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzcHJpbmtsZXIlMjBpcnJpZ2F0aW9uJTIwc3lzdGVtJTIwd2F0ZXJpbmclMjBncmVlbiUyMGxhd258ZW58MHx8fHwxNzcxOTQ2NDMzfDA&ixlib=rb-4.1.0&q=85',
    features: [
      'Custom system design for your property',
      'Premium commercial-grade components',
      'Smart controller installation',
      'Drip irrigation options',
      'Full system warranty'
    ]
  },
  {
    icon: Calendar,
    title: 'Seasonal Maintenance Programs',
    description: 'Keep your irrigation system running at peak efficiency with our comprehensive seasonal maintenance programs. We handle spring startups, mid-season checks, and winterization.',
    image: 'https://images.unsplash.com/photo-1770664945615-52203ab54c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzcHJpbmtsZXIlMjBpcnJpZ2F0aW9uJTIwc3lzdGVtJTIwd2F0ZXJpbmclMjBncmVlbiUyMGxhd258ZW58MHx8fHwxNzcxOTQ2NDMzfDA&ixlib=rb-4.1.0&q=85',
    buttonType: 'packages',
    features: [
      'Spring system activation',
      'Summer efficiency checks',
      'Fall winterization',
      'Controller programming',
      'Priority scheduling'
    ]
  },
  {
    icon: FileText,
    title: 'System Blueprints & Design',
    description: 'Our expert designers create detailed irrigation blueprints that maximize coverage while minimizing water waste. Every design is customized to your specific landscape and water requirements.',
    image: 'https://images.unsplash.com/photo-1712697236445-fad56534129e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxsYW5kc2NhcGUlMjBibHVlcHJpbnRzJTIwYXJjaGl0ZWN0dXJhbCUyMGRyYXdpbmd8ZW58MHx8fHwxNzcxOTQ2NDM2fDA&ixlib=rb-4.1.0&q=85',
    features: [
      'Detailed property assessment',
      'Water pressure analysis',
      'Zone planning optimization',
      'CAD-designed blueprints',
      'Permit-ready documentation'
    ]
  },
  {
    icon: Shield,
    title: 'Back Flow Testing & Certification',
    description: 'Protect your water supply with certified backflow prevention testing. We ensure your system meets all local codes and regulations to keep your family safe.',
    image: 'https://images.pexels.com/photos/4494653/pexels-photo-4494653.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    buttonType: 'comingSoon',
    features: [
      'Certified technicians',
      'Annual testing services',
      'Repair and replacement',
      'Code compliance',
      'Documentation provided'
    ]
  },
  {
    icon: Lightbulb,
    title: 'Landscape Lighting Installation',
    description: 'Transform your outdoor space with professional landscape lighting. We design and install lighting systems that enhance your property\'s beauty and security.',
    image: 'https://images.unsplash.com/photo-1764936509889-a92ac04d9be0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxsYW5kc2NhcGUlMjBsaWdodGluZyUyMHBhdGglMjBsaWdodHMlMjBuaWdodCUyMGdhcmRlbnxlbnwwfHx8fDE3NzE5NDY0MzR8MA&ixlib=rb-4.1.0&q=85',
    features: [
      'Path and walkway lighting',
      'Accent and uplighting',
      'LED energy-efficient options',
      'Timer and smart controls',
      'Low voltage systems'
    ]
  },
    {
    icon: Droplets,
    title: 'Fountains',
    description: 'Add elegance and tranquility to your outdoor space with custom fountain installations. From decorative garden fountains to large water features, we design, install, and maintain stunning fountain systems.',
    image: 'https://customer-assets.emergentagent.com/job_yard-care-expert/artifacts/flvn01dc_Fountain_IMG_1272.png',
    features: [
      'Custom fountain design',
      'Professional installation',
      'Pump and filtration systems',
      'Lighting integration',
      'Ongoing maintenance services'
    ]
  }
  
];

const Services = () => {
  return (
    <div className="pt-24 md:pt-28" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-mist-dark" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-mist-white mb-6">
              Professional Irrigation & <br />
              <span className="text-mist-light">Landscape Solutions</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-light/80 max-w-2xl mx-auto">
              From installation to maintenance, we provide comprehensive services to keep your 
              property looking beautiful and your systems running efficiently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                className={`grid lg:grid-cols-2 gap-12 items-center mb-6 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-14 h-14 bg-mist-light rounded-2xl flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-mist-blue" />
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-mist-dark mb-4">
                    {service.title}
                  </h2>
                  <p className="font-body text-lg text-mist-blue leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-landscape-green flex-shrink-0" />
                        <span className="font-body text-mist-dark">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {service.buttonType === 'comingSoon' ? (
                    <Button 
                      className="bg-red-500/80 hover:bg-red-600 text-white rounded-full px-6 cursor-not-allowed"
                      data-testid={`service-cta-${index}`}
                      disabled
                    >
                      Service Coming Soon
                    </Button>
                  ) : service.buttonType === 'packages' ? (
                    <Link to="/packages">
                      <Button 
                        className="bg-landscape-green hover:bg-landscape-dark text-white rounded-full px-6"
                        data-testid={`service-cta-${index}`}
                      >
                        View Packages
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/contact">
                      <Button 
                        className="bg-landscape-green hover:bg-landscape-dark text-white rounded-full px-6"
                        data-testid={`service-cta-${index}`}
                      >
                        Request Service
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  )}
                </div>
                <div className={`image-zoom rounded-3xl overflow-hidden shadow-2xl ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 lg:h-96 object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-mist-light" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-mist-blue/20 text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
              How We Work
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">
              Our Simple Process
            </h2>
            <p className="font-body text-lg text-mist-blue max-w-2xl mx-auto">
              From initial consultation to project completion, we make the process easy and stress-free.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We assess your property and discuss your needs and goals.' },
              { step: '02', title: 'Design', desc: 'Our experts create a custom plan tailored to your landscape.' },
              { step: '03', title: 'Installation', desc: 'Professional installation using quality parts and techniques.' },
              { step: '04', title: 'Support', desc: 'Ongoing maintenance and support to keep everything running.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white border-0 shadow-lg rounded-2xl h-full" data-testid={`process-step-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="text-5xl font-heading font-bold text-mist-light mb-4">
                      {item.step}
                    </div>
                    <h3 className="font-heading text-xl font-bold text-mist-dark mb-3">
                      {item.title}
                    </h3>
                    <p className="font-body text-mist-blue">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-landscape-green" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Contact us today for a free consultation and estimate on any of our services.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="services-cta-btn"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
